import type {
  C2CQuestionsResponse,
  CompanyJoinRequest,
  CompanyProfile,
  CompanyProfileHistoryEnvelope,
  CompanyToSwitch,
  DirectorySearchPage,
  JoinRequestRole,
  PendingJoinRequest,
} from '@/api/companies/types';
import type {
  C2CQuestion,
  C2CQuestionListParams,
  C2CQuestionPayload,
  DataPointSearchResult,
  PatchTemplatePayload,
  ProfileTemplate,
  TemplateId,
} from '@/api/network/types';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

// Lives on the main API gateway under `/api` (modules architecture),
// NOT on the legacy `/api-service` base — mirrors other migrated routes.
export async function fetchCompaniesToSwitch(): Promise<CompanyToSwitch[]> {
  try {
    return await apiFetch<CompanyToSwitch[]>(
      '/user-companies/companies-to-switch',
      {},
      BACKEND_URL,
    );
  } catch {
    return [];
  }
}

// Read-only company search reachable without a company context — powers the
// onboarding landing page for companyless users. Unlike `searchCompanies`
// (network/-hooks/useSearchCompanies.ts, `/api/companies/vector-search`),
// this has no `excludeCompanyIds` param and no join/connect action.
export async function searchPublicCompanies(
  qs: string,
): Promise<DirectorySearchPage> {
  return apiFetch<DirectorySearchPage>(
    `/api/companies/directory-search?${qs}`,
    {},
    API_ROOT_URL,
  );
}

// Creates a join request for the current (companyless) caller. Lives on the
// main API gateway's bare `/companies` namespace (BACKEND_URL), mirroring
// `fetchCompaniesToSwitch`'s `/user-companies/companies-to-switch` — neither
// route sits behind `/api` or `/v3`. Can 409 when a request is already
// pending or the caller is already a member; the hook decides how to surface
// that. Backend replies via `reply.apiSuccess(result)` — `{ metadata, data }`
// envelope, not the bare payload.
export async function askToJoinCompany(
  companyId: string,
): Promise<{ data: CompanyJoinRequest }> {
  return apiFetch<{ data: CompanyJoinRequest }>(
    '/companies/join-requests',
    { method: 'POST', body: JSON.stringify({ companyId }) },
    BACKEND_URL,
  );
}

/** Caller must be a company admin — the backend scopes this to the caller's
 *  own company. Backend replies via `reply.apiSuccess(result)`, which wraps
 *  the payload as `{ metadata, data }` — `apiFetch` does no unwrapping, so
 *  this returns the envelope; the hook unwraps `.data`. */
export async function fetchPendingJoinRequests(): Promise<{
  data: PendingJoinRequest[];
}> {
  return apiFetch<{ data: PendingJoinRequest[] }>(
    '/companies/join-requests/pending',
    {},
    BACKEND_URL,
  );
}

export async function approveJoinRequest(
  id: string,
  role: JoinRequestRole,
): Promise<void> {
  await apiFetch<void>(
    `/companies/join-requests/${id}/approve`,
    { method: 'POST', body: JSON.stringify({ role }) },
    BACKEND_URL,
  );
}

export async function rejectJoinRequest(id: string): Promise<void> {
  await apiFetch<void>(
    `/companies/join-requests/${id}/reject`,
    { method: 'POST' },
    BACKEND_URL,
  );
}

export async function switchCompany(companyId: string): Promise<void> {
  const raw = localStorage.getItem('retraced:context');
  if (!raw) return;
  const parsed = JSON.parse(raw);
  parsed.state.companyId = companyId;
  localStorage.setItem('retraced:context', JSON.stringify(parsed));
}

// ─── Company profile ──────────────────────────────────────────────────────────

export async function fetchCompanyProfile(
  companyId: string,
): Promise<CompanyProfile> {
  // Lives on the main API gateway (`/api/companies/templates/:templateId/profile/:forCompanyId`),
  // NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
  const res = await apiFetch<CompanyProfile>(
    `/api/companies/templates/about/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
  if (!res) throw new Error('Company not found');
  return res;
}

// Lives on the main API gateway (`/api/companies/:forCompanyId/data-points-answers`),
// NOT on the companies-service microservice — mirrors `fetchCompanyProfile`.
export async function searchDataPointsByName(
  companyId: string,
  qs: string,
): Promise<{ data: DataPointSearchResult[] }> {
  return apiFetch<{ data: DataPointSearchResult[] }>(
    `/api/companies/${companyId}/data-points-answers?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchProfileTemplate(
  templateId: TemplateId,
  companyId: string,
): Promise<ProfileTemplate> {
  // Lives on the main API gateway (`/api/companies/templates/:templateId/profile/:forCompanyId`),
  // NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
  const res = await apiFetch<ProfileTemplate>(
    `/api/companies/templates/${templateId.toLowerCase()}/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
  if (!res) throw new Error('Profile template not found');
  return res;
}

// `GET /api/companies/:id/history` — the caller's own view of the company's
// profile history, keyset-paginated (`cursor`/`limit`/`templateId` in `qs`).
export async function fetchCompanyProfileHistory(
  companyId: string,
  qs: string,
): Promise<CompanyProfileHistoryEnvelope> {
  return apiFetch<CompanyProfileHistoryEnvelope>(
    `/api/companies/${companyId}/history?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export interface AnswerC2CQuestionItem {
  questionId: string;
  answer?: Record<string, unknown>;
  options?: Array<Record<string, unknown>>;
}

export async function answerC2CQuestions(
  companyId: string,
  payload: AnswerC2CQuestionItem[],
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/${encodeURIComponent(companyId)}/c2c-custom-questions/answers`,
    { method: 'PUT', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function fetchC2CQuestions(
  companyId: string,
  params: { page: number; count: number },
): Promise<C2CQuestionsResponse> {
  const qs = new URLSearchParams({
    page: String(params.page),
    count: String(params.count),
  });
  return apiFetch<C2CQuestionsResponse>(
    `/api/companies/${encodeURIComponent(companyId)}/c2c-custom-questions?${qs.toString()}`,
    {},
    API_ROOT_URL,
  );
}

// ─── C2C Builder (own-company field management) ──────────────────────────────

export async function fetchC2CQuestionsList(
  params: C2CQuestionListParams,
): Promise<C2CQuestionsResponse> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.count != null) qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return apiFetch<C2CQuestionsResponse>(
    `/api/c2c-custom-questions?${qs.toString()}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchC2CQuestionById(
  id: string,
): Promise<{ data: C2CQuestion }> {
  return apiFetch<{ data: C2CQuestion }>(
    `/api/c2c-custom-questions/${encodeURIComponent(id)}`,
    {},
    API_ROOT_URL,
  );
}

export async function createC2CQuestion(
  payload: C2CQuestionPayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/c2c-custom-questions',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function updateC2CQuestion(
  id: string,
  payload: Pick<
    C2CQuestionPayload,
    'title' | 'hintText' | 'apiFieldIdentifier'
  >,
): Promise<void> {
  await apiFetch<void>(
    `/api/c2c-custom-questions/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function deleteC2CQuestions(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/c2c-custom-questions/bulk-remove',
    { method: 'PUT', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

export async function patchProfileTemplate(
  templateId: TemplateId,
  payload: PatchTemplatePayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/templates/${templateId.toLowerCase()}/answer`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}
